import React from 'react';

import { isMobileDevice } from '@eluve/utils';

import { VideoWalkthroughFlow } from '../flows/VideoWalkthroughFlow/VideoWalkthroughFlow';
import { VideoWalkthroughSidePanel } from '../flows/VideoWalkthroughFlow/VideoWalkthroughSidePanel';
import { useInterstitialFlow } from '../hooks/useInterstitialFlow';
import {
  BaseInterstitial,
  InterstitialContext,
  InterstitialFlowProps,
} from '../types';

const ELIGIBILITY_DATE = '2025-09-01T00:00:00Z';

/**
 * Shared logic for determining if the video walkthrough should be shown
 * This handles all the business rules except device type (mobile vs desktop)
 */
const shouldShowVideoWalkthrough = (context: InterstitialContext): boolean => {
  // Only show for users who signed up after the eligibility date
  if (context.userSession?.createdAt) {
    const userCreatedAt = new Date(
      context.userSession.createdAt * 1000,
    ).toISOString();
    if (userCreatedAt < ELIGIBILITY_DATE) {
      return false;
    }
  }

  // Exclude siloam hospital users
  if (context.userSession?.email?.endsWith('@siloamhospitals.com')) {
    return false;
  }

  // Only show if user registration has been completed
  const userRegistrationCompletion = context.data.userInterstitialFlows.find(
    (c) => c.flowName === 'USER_REGISTRATION',
  );
  if (!userRegistrationCompletion?.completedAt) {
    return false;
  }

  const videoWalkthroughCompletion = context.data.userInterstitialFlows.find(
    (c) => c.flowName === 'VIDEO_WALKTHROUGH',
  );

  return (
    !videoWalkthroughCompletion?.completedAt &&
    !videoWalkthroughCompletion?.skippedAt
  );
};

/**
 * Component that handles video walkthrough flow
 */
const VideoWalkthroughFlowWithBusinessLogic: React.FC<
  InterstitialFlowProps
> = ({ context }) => {
  const { complete } = useInterstitialFlow('VIDEO_WALKTHROUGH');

  return <VideoWalkthroughFlow context={context} onComplete={complete} />;
};

export const VideoWalkthroughInterstitial: BaseInterstitial = {
  name: 'VIDEO_WALKTHROUGH',
  component: VideoWalkthroughFlowWithBusinessLogic,
  sidepanelComponent: VideoWalkthroughSidePanel,
  priority: 90,
  transparentBackground: true,

  showOnPaths: [/^\/organizations\/[^/]+\/home$/],

  shouldShow: (context: InterstitialContext): boolean => {
    if (!isMobileDevice) {
      return false;
    }

    return shouldShowVideoWalkthrough(context);
  },

  shouldShowSidePanel: (context: InterstitialContext): boolean => {
    if (isMobileDevice) {
      return false;
    }

    return shouldShowVideoWalkthrough(context);
  },

  canSkip: (): boolean => {
    return true;
  },
};
